'use client';

import {useCallback, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {ArrowLeftIcon, ArrowRightIcon, XIcon} from 'lucide-react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  TinctIcon,
} from '@tinct/web-components';
import {useGetCampaignById} from '@tinct/core-api';
import {EmbeddedPage} from '@tinct/core-api-models';
import {useCurrentOrganization, useGetCurrentWorkspaceId, useSubscriptionPlan} from '@tinct/core-workspace';

import {ENTERPRISE_SALES_URL, usePaywallHash} from './hooks/use-paywall-hash';
import {usePaywallProducts} from './hooks/use-paywall-products';
import {usePaywallCheckout} from './hooks/use-paywall-checkout';
import {PaywallAlert} from './components/paywall-alert';
import {CompactPlanSummary} from './components/compact-plan-summary';
import {UpgradeSection} from './components/upgrade-section';
import {VariantPagesView} from './components/variant-pages-view';
import {PaywallFooter} from './components/paywall-footer';

export type {PaywallReason, UseOpenPaywallOptions} from './hooks/use-paywall-hash';
export {useOpenPaywall} from './hooks/use-paywall-hash';

export type PaywallProRequiredModalProps = {
  hashKey?: string;
  onUpgrade?: (selectedPriceId: string) => void;
};

export function PaywallProRequiredModal({
  hashKey = 'paywall-pro',
  onUpgrade,
}: PaywallProRequiredModalProps) {
  const currentOrganizationId = useGetCurrentWorkspaceId();
  const currentOrganization = useCurrentOrganization();
  const { planName, hasActiveSubscription, subscriptionBadgeStatusProp, subscriptionBadgeStatusLabel } = useSubscriptionPlan();

  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
  const [isAnnual, setIsAnnual] = useState(true);
  const [viewMode, setViewMode] = useState<'pricing' | 'pages'>('pricing');

  const location = useLocation();
  const navigate = useNavigate();

  const { hashParams, isOpen, isNotEnoughCredits, isNotEnoughLivePages, showPlanSummary, isSalesOnly } = usePaywallHash(hashKey);
  const { planOptions, selectedProduct, selectedPrice, displayPrice, annualSavings, features } = usePaywallProducts(selectedProductId, isAnnual);
  const { createCheckoutSession, isCheckoutPending } = usePaywallCheckout();

  // Fetch campaign pages for the variant pages view
  const { data: paywallCampaign } = useGetCampaignById(
    {
      id: hashParams?.campaignId ?? '',
      fields: {
        pages: {
          limit: 1000,
          fields: {
            id: null,
            status: null,
            lead: {
              fields: {
                company: {
                  fields: {
                    name: null,
                    logo: null,
                    enrichmentScore: null,
                  },
                },
              },
            },
          },
        },
      },
    },
    { enabled: !!hashParams?.campaignId && isNotEnoughLivePages },
  );

  const creditQuota = currentOrganization.activeQuotas?.find(q => q.featureKey === 'lp_generation_credit');
  const lpQuota = currentOrganization.activeQuotas?.find(q => q.featureKey === 'lp_publication');

  const handleClose = useCallback(() => {
    setViewMode('pricing');
    navigate({ hash: '' }, { replace: true });
  }, [navigate]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) handleClose();
    },
    [handleClose],
  );

  const handlePublishPartial = useCallback(() => {
    navigate({ hash: 'publish-partial' }, { replace: true });
  }, [navigate]);

  const handleUpgrade = () => {
    if (selectedPrice?.id && currentOrganizationId) {
      const baseUrl = window.location.origin;
      const currentPath = location.pathname;

      createCheckoutSession({
        organizationId: currentOrganizationId,
        priceId: selectedPrice.id,
        successUrl: `${baseUrl}${currentPath}#checkout-succeed`,
        cancelUrl: `${baseUrl}${currentPath}?checkout=canceled`,
      });

      onUpgrade?.(selectedPrice.id);
    }
  };

  const modalTitle = isSalesOnly
    ? 'Available on Enterprise'
    : viewMode === 'pages'
    ? 'Monthly limit reached : variant pages'
    : isNotEnoughCredits
      ? 'Monthly limit reached: credit limit'
      : isNotEnoughLivePages
        ? 'Monthly limit reached : page variants'
        : 'You need to upgrade';

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-2xl gap-6 rounded-3xl p-8" showCloseButton={false}>
        <DialogHeader className="flex-row items-start gap-4">
          <TinctIcon className="size-10 shrink-0" />
          <DialogTitle className="grow font-heading text-xl tracking-[-0.4px]">
            {modalTitle}
          </DialogTitle>
          <button
            onClick={handleClose}
            className="shrink-0 opacity-70 transition-opacity hover:opacity-100"
          >
            <XIcon className="size-6" />
            <span className="sr-only">Close</span>
          </button>
        </DialogHeader>

        <div className="flex flex-col gap-6 -mx-4 no-scrollbar max-h-[75vh] overflow-y-auto px-4">
          <PaywallAlert
            hashParams={hashParams}
            isNotEnoughCredits={isNotEnoughCredits}
            isNotEnoughLivePages={isNotEnoughLivePages}
            creditQuota={creditQuota}
            lpQuota={lpQuota}
          />

          {viewMode === 'pages' ? (
            <>
              <Button
                variant="link"
                className="gap-1 self-start p-0 text-sm font-medium"
                onClick={() => setViewMode('pricing')}
              >
                <ArrowLeftIcon className="size-4" />
                Back to pricing
              </Button>

              <VariantPagesView
                pages={(paywallCampaign?.pages ?? []) as EmbeddedPage[]}
                remainingQuota={lpQuota?.remainingQuota ?? 0}
                totalPages={hashParams?.totalPages ?? 0}
              />
            </>
          ) : (
            <>
              {isNotEnoughLivePages && hashParams?.campaignId && (
                <div className="flex justify-end">
                  <Button
                    variant="link"
                    className="gap-1 p-0 text-sm font-medium"
                    onClick={() => setViewMode('pages')}
                  >
                    See the {hashParams.totalPages} page variants
                    <ArrowRightIcon className="size-4" />
                  </Button>
                </div>
              )}

              {showPlanSummary && (
                <CompactPlanSummary
                  planName={planName}
                  isPro={hasActiveSubscription}
                  badgeStatusVariant={subscriptionBadgeStatusProp}
                  badgeStatusLabel={subscriptionBadgeStatusLabel}
                  creditQuota={creditQuota}
                  lpQuota={lpQuota}
                  subscription={currentOrganization.subscription}
                />
              )}

              {/* No self-serve price covers this reason: the alert above says what is missing. */}
              {!isSalesOnly && (
                <UpgradeSection
                  displayPrice={displayPrice}
                  isAnnual={isAnnual}
                  onAnnualChange={setIsAnnual}
                  annualSavings={annualSavings}
                  planOptions={planOptions}
                  selectedProductId={(selectedProduct?.id as string) ?? undefined}
                  onProductChange={setSelectedProductId}
                  features={features}
                />
              )}
            </>
          )}
        </div>

        <PaywallFooter
          salesUrl={isSalesOnly ? ENTERPRISE_SALES_URL : undefined}
          isNotEnoughLivePages={isNotEnoughLivePages}
          remainingQuota={lpQuota?.remainingQuota ?? 0}
          isCheckoutPending={isCheckoutPending}
          onClose={handleClose}
          onPublishPartial={handlePublishPartial}
          onUpgrade={handleUpgrade}
        />
      </DialogContent>
    </Dialog>
  );
}
